import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling, ServiceError } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';

export interface AnalyticsTimeRange {
  startDate: string;
  endDate: string;
}

export interface PostAnalyticsSummary {
  totalPosts: number;
  publishedPosts: number;
  scheduledPosts: number;
  draftPosts: number;
  totalEngagement: number;
  averageEngagement: number;
  topPerformer: {
    id: string;
    content: string;
    engagement: number;
  } | null;
}

export class AnalyticsService {
  private static readonly logger = logger.child({ service: 'AnalyticsService' });

  /**
   * Get post analytics summary for an organization
   */
  static async getPostSummary(
    organizationId: string,
    timeRange?: AnalyticsTimeRange
  ): Promise<PostAnalyticsSummary> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching post summary', { organizationId, timeRange });

      let postsQuery = supabase
        .from('posts')
        .select('id, content, status, created_at')
        .eq('organization_id', organizationId);

      if (timeRange) {
        postsQuery = postsQuery
          .gte('created_at', timeRange.startDate)
          .lte('created_at', timeRange.endDate);
      }

      const { data: posts, error: postsError } = await postsQuery;
      if (postsError) throw postsError;

    const totalPosts = posts?.length || 0;
    const publishedPosts = posts?.filter(p => p.status === 'published').length || 0;
    const scheduledPosts = posts?.filter(p => p.status === 'scheduled').length || 0;
    const draftPosts = posts?.filter(p => p.status === 'draft').length || 0;

    // Get analytics for published posts
    const publishedPostIds = posts?.filter(p => p.status === 'published').map(p => p.id) || [];

    if (publishedPostIds.length === 0) {
      return {
        totalPosts,
        publishedPosts,
        scheduledPosts,
        draftPosts,
        totalEngagement: 0,
        averageEngagement: 0,
        topPerformer: null,
      };
    }

      const { data: analytics, error: analyticsError } = await supabase
        .from('post_analytics')
        .select('post_id, likes, comments, shares')
        .in('post_id', publishedPostIds);

      if (analyticsError) throw analyticsError;

      // Calculate engagement metrics
      const engagementByPost = new Map<string, number>();
      let totalEngagement = 0;

      analytics?.forEach(a => {
        const engagement = (a.likes || 0) + (a.comments || 0) + (a.shares || 0);
        engagementByPost.set(a.post_id, (engagementByPost.get(a.post_id) || 0) + engagement);
        totalEngagement += engagement;
      });

      // Find top performer
      let topPerformer = null;
      let maxEngagement = 0;

      engagementByPost.forEach((engagement, postId) => {
        if (engagement > maxEngagement) {
          maxEngagement = engagement;
          const post = posts?.find(p => p.id === postId);
          if (post) {
            topPerformer = {
              id: postId,
              content: post.content,
              engagement,
            };
          }
        }
      });

      const summary = {
        totalPosts,
        publishedPosts,
        scheduledPosts,
        draftPosts,
        totalEngagement,
        averageEngagement: publishedPosts > 0 ? totalEngagement / publishedPosts : 0,
        topPerformer,
      };

      this.logger.info('Post summary calculated', { organizationId, ...summary });

      return summary;
    }, { operation: 'getPostSummary', table: 'posts' });
  }

  /**
   * Get link analytics
   */
  static async getLinkAnalytics(linkId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching link analytics', { linkId });

      const { data: clicks, error } = await supabase
        .from('link_clicks')
        .select('*')
        .eq('short_link_id', linkId)
        .order('clicked_at', { ascending: false });

      if (error) throw error;

      const totalClicks = clicks?.length || 0;
      const uniqueVisitors = new Set(clicks?.map(c => c.ip_address)).size;

      // Aggregate device types
      const devices: Record<string, number> = {};
      clicks?.forEach(c => {
        if (c.device_type) {
          devices[c.device_type] = (devices[c.device_type] || 0) + 1;
        }
      });

      // Aggregate countries
      const countries: Record<string, number> = {};
      clicks?.forEach(c => {
        if (c.country) {
          countries[c.country] = (countries[c.country] || 0) + 1;
        }
      });

      const analytics = {
        totalClicks,
        uniqueVisitors,
        devices,
        topCountries: Object.entries(countries)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5),
        recentClicks: clicks?.slice(0, 10) || [],
      };

      this.logger.info('Link analytics calculated', { linkId, totalClicks });

      return analytics;
    }, { operation: 'getLinkAnalytics', table: 'link_clicks' });
  }

  /**
   * Get best posting times for an organization
   */
  static async getBestPostingTimes(organizationId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Analyzing best posting times', { organizationId });

      const { data, error } = await supabase.functions.invoke('analyze-best-times', {
        body: { organizationId },
      });

      if (error) throw ServiceError.internal('Failed to analyze best times', { originalError: error });

      this.logger.info('Best posting times analyzed', { organizationId });

      return data;
    }, { operation: 'getBestPostingTimes' });
  }

  /**
   * Get performance insights
   */
  static async getPerformanceInsights(organizationId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching performance insights', { organizationId });

      const { data, error } = await supabase.functions.invoke('analyze-performance-insights', {
        body: { organizationId },
      });

      if (error) throw ServiceError.internal('Failed to get performance insights', { originalError: error });

      this.logger.info('Performance insights fetched', { organizationId });

      return data;
    }, { operation: 'getPerformanceInsights' });
  }

  /**
   * Get platform comparison data
   */
  static async getPlatformComparison(
    organizationId: string,
    dateRange?: { startDate: string; endDate: string }
  ) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching platform comparison', { organizationId, dateRange });

      let query = supabase
        .from('posts')
        .select(`
          id,
          platform,
          status,
          published_at,
          post_analytics (likes, comments, shares, clicks, engagement_rate)
        `)
        .eq('organization_id', organizationId)
        .eq('status', 'published');

      if (dateRange) {
        query = query
          .gte('published_at', dateRange.startDate)
          .lte('published_at', dateRange.endDate);
      }

      const { data: posts, error } = await query;

      if (error) throw error;

      // Group by platform
      const platformMap = new Map<string, {
        posts: number;
        engagement: number;
        clicks: number;
        engagementRates: number[];
      }>();

      posts?.forEach(post => {
        const platform = post.platform.toLowerCase();
        const current = platformMap.get(platform) || {
          posts: 0,
          engagement: 0,
          clicks: 0,
          engagementRates: [],
        };

        current.posts += 1;

        if (post.post_analytics && post.post_analytics.length > 0) {
          const analytics = post.post_analytics[0];
          current.engagement += (analytics.likes || 0) + (analytics.comments || 0) + (analytics.shares || 0);
          current.clicks += analytics.clicks || 0;
          if (analytics.engagement_rate) {
            current.engagementRates.push(Number(analytics.engagement_rate));
          }
        }

        platformMap.set(platform, current);
      });

      // Convert to array format
      const comparison = Array.from(platformMap.entries()).map(([platform, data]) => ({
        platform,
        posts: data.posts,
        engagement: data.engagement,
        clicks: data.clicks,
        engagementRate: data.engagementRates.length > 0
          ? data.engagementRates.reduce((a, b) => a + b, 0) / data.engagementRates.length
          : 0,
      }));

      this.logger.info('Platform comparison calculated', { 
        organizationId, 
        platforms: comparison.length 
      });

      return comparison;
    }, { operation: 'getPlatformComparison', table: 'posts' });
  }

  /**
   * Calculate campaign ROI metrics
   */
  static async calculateCampaignROI(campaignId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Calculating campaign ROI', { campaignId });

      // Get campaign data
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .select('*, campaign_posts(post_id)')
        .eq('id', campaignId)
        .maybeSingle();

      if (campaignError) throw campaignError;

      if (!campaign) {
        throw ServiceError.notFound('Campaign', campaignId);
      }

      // Get trial attribution data (if table exists)
      const postIds = campaign.campaign_posts?.map(cp => cp.post_id) || [];
      
      let trialsGenerated = 0;
      let conversions = 0;
      let conversionRate = 0;

      // Note: Trial tracking would require proper table setup
      // For now, defaulting to 0

      const roi = {
        campaignId,
        campaignName: campaign.name,
        trialsGenerated,
        conversions,
        conversionRate,
        postCount: postIds.length,
      };

      this.logger.info('Campaign ROI calculated', { campaignId });

      return roi;
    }, { operation: 'calculateCampaignROI', table: 'campaigns' });
  }
}
